import { isDefined } from "@clipboard-health/util-ts";
import { intersection, pick } from "lodash";

// Inline type to avoid restricted launchdarkly-react-client-sdk import
type LDFlagSet = Record<string, unknown>;

import { FacilityRoles } from "../types";
import {
  ALERT_TYPE,
  type FacilityNotification,
  type FacilityNotificationAlertType,
  type TypeAlertSettings,
  type WorkplaceNotificationsMap,
} from "./types";
import {
  CHARGE_RATE_VISIBLE_EMAIL,
  getDefaultWorkplaceNotifications,
} from "./workplaceNotificationDefaults";

function filterFacilityNotificationByRolesAndFlags(
  facilityNotifications: WorkplaceNotificationsMap,
  roles: string[] | undefined,
  flags?: LDFlagSet
): WorkplaceNotificationsMap {
  const values = Object.entries(facilityNotifications).map(([configKey, config]) => {
    const configValues = Object.entries(config)
      .map(([subConfigKey, subConfig]) => {
        if (subConfig.lddFlag && !flags?.[subConfig.lddFlag]) {
          return undefined;
        }
        if (
          subConfigKey !== CHARGE_RATE_VISIBLE_EMAIL ||
          intersection(roles, [FacilityRoles.INVOICES, FacilityRoles.ADMIN]).length === 0
        ) {
          return [subConfigKey, subConfig] as const;
        }

        return undefined;
      })
      .filter((subConfig) => isDefined(subConfig));
    return [configKey, Object.fromEntries(configValues)] as const;
  });
  return Object.fromEntries(values) as WorkplaceNotificationsMap;
}

function getAlertTypeDefaultFromAuthorization(
  originalNotification: FacilityNotificationAlertType,
  roles: string[],
  permissions: string[]
): Partial<FacilityNotificationAlertType["default"]> {
  const notification: Partial<FacilityNotificationAlertType["default"]> = {};

  if (originalNotification.disableBatching) {
    notification.batch = false;
    notification.userModifiableBatch = false;
  }

  if (originalNotification?.mandatoryRoles?.length) {
    if (
      intersection(originalNotification.mandatoryRoles, roles).length ||
      intersection(originalNotification.mandatoryPermissions, permissions).length
    ) {
      notification.batch = false;
      notification.userModifiableBatch = false;
      notification.userModifiableState = false;
      notification.enabled = true;
    } else {
      notification.userModifiableState = originalNotification.default.userModifiableState;
    }
  }

  return notification;
}

function isAlertType(key: string): key is ALERT_TYPE {
  return Object.values(ALERT_TYPE).includes(key as ALERT_TYPE);
}

function overrideFacilityNotificationsAlertSettings(
  data: WorkplaceNotificationsMap,
  userGranularSettings: Partial<TypeAlertSettings> | undefined,
  roles: string[] | undefined,
  useDefaultEnabledState: boolean,
  flags?: LDFlagSet,
  permissions?: string[]
): Record<ALERT_TYPE, FacilityNotification[]> {
  const notificationEntries = Object.entries(data).map<[ALERT_TYPE, FacilityNotification[]]>(
    ([alertType, config]) => {
      if (!isAlertType(alertType)) {
        throw new Error(`Invalid alert type: ${alertType}`);
      }

      const alerts = Object.entries(config)
        .map(([_role, item]) => item.alertTypes)
        .flat()
        .filter((notification) => {
          if (notification?.lddFlag && !flags?.[notification.lddFlag]) {
            return false;
          }

          const hasRoles = intersection(notification?.validRoles, roles).length > 0;
          const hasPermissions =
            intersection(notification.validPermissions, permissions).length > 0;
          return hasRoles || hasPermissions;
        })
        .map((notificationDefaults) => {
          const notificationWithPermissionApplied = getAlertTypeDefaultFromAuthorization(
            notificationDefaults,
            roles ?? [],
            permissions ?? []
          );

          const notificationUserConfig =
            userGranularSettings?.[notificationDefaults.type]?.[notificationDefaults.action];
          if (!notificationUserConfig) {
            // ewtrEmails was not included in the backend's default alert settings map when
            // facility users were created, so existing users have no saved preference for it.
            // The backend treats undefined/null ewtrEmails as enabled, so the UI should
            // also show it as enabled by default to match the actual notification behavior.
            const shouldUseDefaultEnabled =
              useDefaultEnabledState ||
              notificationDefaults.action === "ewtrEmails" ||
              notificationDefaults.action === "wleReplacementEmail";
            return {
              ...notificationDefaults,
              default: {
                ...notificationDefaults.default,
                ...notificationWithPermissionApplied,
                enabled: shouldUseDefaultEnabled
                  ? (notificationWithPermissionApplied.enabled ??
                    notificationDefaults.default.enabled)
                  : false,
              },
            };
          }

          return {
            ...notificationDefaults,
            default: {
              // NOTE:: Do not add `enable` and `batch` parameters to below list.
              ...pick(notificationDefaults.default, [
                "userModifiableState",
                "userModifiableBatch",
                "disableBatching",
                "tempBatchDisable",
                "time",
              ]),
              ...notificationUserConfig,
              ...notificationWithPermissionApplied,
            },
          };
        });

      return [alertType, alerts];
    }
  );

  return Object.fromEntries(notificationEntries) as Record<ALERT_TYPE, FacilityNotification[]>;
}

export function fillDefaultAlertSettings(
  userGranularSettings: Partial<TypeAlertSettings> | undefined,
  roles: string[] | undefined,
  useDefaultEnabledState: boolean,
  flags?: LDFlagSet,
  permissions?: string[]
): Record<ALERT_TYPE, FacilityNotification[]> {
  const filteredFacilityNotification = filterFacilityNotificationByRolesAndFlags(
    getDefaultWorkplaceNotifications(),
    roles,
    flags
  );

  const notifyValues = overrideFacilityNotificationsAlertSettings(
    filteredFacilityNotification,
    userGranularSettings,
    roles,
    useDefaultEnabledState,
    flags,
    permissions
  );

  const allNotifications = {
    default: {
      userModifiableState: true,
      userModifiableBatch: false,
      batch: false,
      time: 16,
    },
    title: "All",
    action: "All",
  };

  notifyValues.EMAIL = [
    {
      ...allNotifications,
      type: ALERT_TYPE.EMAIL,
      default: {
        enabled: userGranularSettings?.[ALERT_TYPE.EMAIL]?.All?.enabled ?? true,
        ...allNotifications.default,
      },
    },
    ...notifyValues.EMAIL,
  ];

  notifyValues.SMS = [
    {
      ...allNotifications,
      default: {
        enabled: userGranularSettings?.[ALERT_TYPE.SMS]?.All?.enabled ?? true,
        ...allNotifications.default,
      },
      type: ALERT_TYPE.SMS,
    },
    ...notifyValues.SMS,
  ];

  return notifyValues;
}
